import React, { useEffect, useRef, useState } from "react";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import { referralLink } from "./ReferEarnSheet";
import { fansApi } from "../services/fans";
import type { FanReferralStats } from "../types/fans";
import { copyTextToClipboard } from "../lib/clipboard";
import { useFitText } from "../hooks/useFitText";
import { t, formatLocale } from "../i18n";
import { T } from "../i18n/T";
import FanCashoutFlow, { withdrawLine } from "./FanCashoutFlow";
import { formatAmountForCopy, formatTxnAmount } from "../utils/currency";
import "./ReferEarnSheet.css";
import "./FanReferEarnSheet.css";

// Fan Refer & Earn (Profile sheet → Refer & Earn), part 1 — founder 10/29,
// spec client/.claude/fan-refer-earn-plan.html. The talent page's own layout
// and CSS (ReferEarnSheet): the big number on top, the faint chart while it's
// zero, "Your referrals", the code pill with Copy link, "Your terms". Only
// the "Know a Band or DJ?" line and the gold points are the fan's.
//   • ★500 points per band or DJ the founder APPROVES (server
//     lib/fanReferrals.js) — the number comes from the server (starsEach).
//   • Link only: no QR, no Share button (founder 10/29, like the talent page).
//   • My referrals: name · joined, the points once approved, else a grey
//     "In review" tag.
// Codes are not case sensitive; they are always SHOWN and TYPED in caps.

const SITE = "requesta.app";

interface Props {
  open: boolean;
  onClose: () => void;
}

const joinedOn = (iso: string) =>
  new Date(iso).toLocaleDateString(formatLocale(), { month: "short", day: "numeric" });
const n = (v: number) => v.toLocaleString(formatLocale());

const svg = (d: React.ReactNode, size = 22, sw = 2) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const CHECK_ICON = svg(<path d="M5 12.5l4.5 4.5L19 7.5" />, 13, 3);
const PEOPLE_ICON = svg(
  <>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0111 0" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15.5 14.2A4.6 4.6 0 0121 19" />
  </>,
  24
);
const INFO_ICON = svg(<><circle cx="12" cy="12" r="9.5" /><path d="M12 11v5.5" /><path d="M12 7.6v.2" /></>, 20, 1.7);
const COPY_ICON = svg(<><rect x="8.5" y="8.5" width="11" height="11" rx="2.5" /><path d="M15.5 8.5V6.5a2 2 0 00-2-2h-7a2 2 0 00-2 2v7a2 2 0 002 2h2" /></>, 17, 2);
const PENCIL_ICON = svg(<><path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></>, 14, 2.2);
const CHEVRON_UP = svg(<path d="M6 15l6-6 6 6" />, 18, 2.2);
// The points star (gold, filled) — a drawn shape, never the Apple emoji.
const STAR = (size: number) => (
  <svg className="fre-star" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.8l2.75 5.9 6.45.75-4.78 4.4 1.3 6.37L12 17l-5.72 3.22 1.3-6.37L2.8 9.45l6.45-.75z" fill="currentColor" />
  </svg>
);
const gold = (c: React.ReactNode) => <b className="fre-gold">{c}</b>;
const green = (c: React.ReactNode) => <b className="fre-green">{c}</b>;
const usd = (v: number) => formatTxnAmount(v, "usd");


export default function FanReferEarnSheet({ open, onClose }: Props) {
  const [stats, setStats] = useState<FanReferralStats | null>(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const [view, setView] = useState<"code" | "people">("code");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [cashingOut, setCashingOut] = useState(false);

  // Fresh every open: an approval since last time shows at once.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setFailed(false);
    fansApi
      .referrals()
      .then((s) => alive && setStats(s))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [open, tick]);

  const code = (stats?.code || "").toUpperCase();
  const heroRef = useRef<HTMLSpanElement>(null);
  // Part 2 (server lib/fanCashout.js): the fan's 2% in dollars, once switched on.
  const money = stats?.money?.enabled ? stats.money : null;
  useFitText(heroRef, `${stats?.starsEarned}|${money?.earned}|${open}|${view}`, 22);
  const compactCopy = code.length > 11 && typeof window !== "undefined" && window.innerWidth < 360;
  const codeRef = useRef<HTMLSpanElement>(null);
  useFitText(codeRef, `${code}|${open}|${view}|${editing}`, 10);

  const close = () => {
    setView("code");
    setEditing(false);
    setError("");
    onClose();
  };

  const copy = async () => {
    await copyTextToClipboard(referralLink(code));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    const next = draft.trim().toLowerCase();
    if (!/^[a-z0-9]{4,15}$/.test(next)) {
      setError(t("referEarnSheet.lettersOrNumbers"));
      return;
    }
    setSaving(true);
    try {
      const res = await fansApi.setReferralCode(next);
      setStats((s) => (s ? { ...s, code: res.code } : s));
      setEditing(false);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.msg || t("referEarnSheet.thatDidnTSave"));
    } finally {
      setSaving(false);
    }
  };

  if (open && view === "people" && stats) {
    return (
      <BottomSheet open={open} title={t("referEarnSheet.myReferrals")} icon={PROFILE_ICONS.refer} onClose={close} tall>
        <div className="re-sheet">
          <button type="button" className="re-back" onClick={() => setView("code")}>
            {t("referEarnSheet.referEarn")}
          </button>
          <div className="re-total">
            <span>{t("referEarnSheet.joined", { joined: stats.joined })}</span>
            <b className="fre-gold">{t("fanReferEarn.starsTotal", { stars: n(stats.starsEarned) })}</b>
          </div>
          {stats.people.length === 0 && <div className="re-empty">{t("referEarnSheet.noOneHasJoined")}</div>}
          {stats.people.map((p, i) => (
            <div className="re-person" key={`${p.name}-${p.joinedAt}-${i}`}>
              <div className="re-person-main">
                <div className="re-person-name">{p.name}</div>
                <div className="re-person-sub">{t("fanReferEarn.joinedOn", { joinedOn: joinedOn(p.joinedAt) })}</div>
              </div>
              {p.approved ? (
                <div className="re-person-amt fre-gold">★{n(p.stars)}</div>
              ) : (
                <span className="fre-inrev">{t("fanReferEarn.inReview")}</span>
              )}
            </div>
          ))}
        </div>
      </BottomSheet>
    );
  }

  return (
    <>
    <BottomSheet open={open} title={t("referEarnSheet.referEarn2")} icon={PROFILE_ICONS.refer} onClose={close} tall className="re-bsheet">
      <div className="re-sheet re-v2">
        <div className="fre-head">
          <div className="fre-h">{t("fanReferEarn.headline")}</div>
          <div className="fre-s">
            {money ? (
              <T k="fanReferEarn.earnPctPlusPoints" vars={{ stars: n(stats?.starsEach ?? 500) }} tags={{ 0: green, 1: gold }} />
            ) : (
              <T k="fanReferEarn.getStars" vars={{ stars: n(stats?.starsEach ?? 500) }} tags={{ 0: gold }} />
            )}
          </div>
        </div>

        {failed && !stats ? (
          <div className="re-empty">
            <T k="fanReferEarn.couldntLoad" tags={{ 0: (c) => <button type="button" className="fre-retry" onClick={() => setTick((x) => x + 1)}>{c}</button> }} />
          </div>
        ) : !stats ? (
          <div className="fre-loading" aria-hidden="true" />
        ) : (
          <>
            {money ? (
              <>
                {/* The money on top (founder pick 1): Total earned in dollars,
                    points under it, then Cash out — or, under $20, one thin
                    bar — and where the money can go. */}
                {/* Founder 10/30, pick A on .claude/fan-under20-calmer.html:
                    points beside "Total earned"; under $20 only a bare bar and
                    one small line; Cash out and "Cash out to …" at $20. */}
                <button type="button" className="re-hero fre-hero-money" onClick={() => setView("people")}>
                  <span className="re-hero-k fre-hero-k">
                    {t("referEarnSheet.totalEarned")}
                    <span className="fre-hero-pts">{STAR(14)}<b>{n(stats.starsEarned)}</b> {t("fanCashout.points")}</span>
                  </span>
                  <span className="re-hero-v" ref={heroRef}>{usd(money.earned || 0)}</span>
                </button>
                {money.canCashOut ? (
                  <>
                    <button type="button" className="fre-cashout" onClick={() => setCashingOut(true)}>{t("fanCashout.cashOut")}</button>
                    <p className="fre-line">{t("fanCashout.cashOutTo", { list: withdrawLine(money.withdrawTo) })}</p>
                  </>
                ) : (
                  <>
                    <div className="fre-bar" aria-hidden="true">
                      <i style={{ width: `${Math.min(100, Math.round(((money.ready || 0) / (money.minimum || 20)) * 100))}%` }} />
                    </div>
                    <p className="fre-togo">
                      {t("fanCashout.moreToCashOut", { amount: usd(Math.max(0, (money.minimum || 20) - (money.ready || 0))) })}
                    </p>
                  </>
                )}
              </>
            ) : (
              <button type="button" className="re-hero" onClick={() => setView("people")}>
                <span className="re-hero-k">{t("fanReferEarn.starsEarned")}</span>
                <span className="re-hero-v" ref={heroRef}>
                  {STAR(30)}
                  {n(stats.starsEarned)}
                </span>
              </button>
            )}

            {!money && stats.starsEarned === 0 && (
              <div className="re-chart">
                <svg viewBox="0 0 340 120" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="fre-chart-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#ffffff" stopOpacity="0.05" />
                      <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0 78 L34 80 L68 85 L101 87 L135 92 L169 90 L202 84 L236 78 L270 74 L303 34 L340 38 L340 120 L0 120 Z" fill="url(#fre-chart-fill)" />
                  <path d="M0 78 L34 80 L68 85 L101 87 L135 92 L169 90 L202 84 L236 78 L270 74 L303 34 L340 38" fill="none" stroke="#3a4252" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                </svg>
                <span className="re-chart-pill">{t("fanReferEarn.starsWillAppear")}</span>
              </div>
            )}

            <button type="button" className="re-refs" onClick={() => setView("people")}>
              <span className="re-refs-ic">{PEOPLE_ICON}</span>
              <span className="re-refs-k">{t("referEarnSheet.yourReferrals")}</span>
              <b className="re-refs-v">{stats.joined}</b>
              <span className="re-refs-chev" aria-hidden="true">›</span>
            </button>

            {editing ? (
              <div className="re-edit">
                <div className="re-code-lbl">{t("referEarnSheet.yourCode")}</div>
                <input
                  className="re-code-input"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 15));
                    setError("");
                  }}
                  aria-label={t("referEarnSheet.yourCode2")}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                />
                <div className="re-code-link">
                  <T k="referEarnSheet.join" vars={{ SITE, value: draft.toLowerCase() }} tags={{ 0: (c) => <b>{c}</b> }} />
                </div>
                <div className={`re-code-hint${error ? " bad" : ""}`} role={error ? "alert" : undefined}>
                  {error || t("referEarnSheet.codeRules")}
                </div>
                <div className="re-edit-btns">
                  <button type="button" className="re-mini re-mini--ghost" onClick={() => { setEditing(false); setError(""); }}>
                    {t("referEarnSheet.cancel")}
                  </button>
                  <button type="button" className="re-mini" onClick={save} disabled={saving}>
                    {saving ? t("referEarnSheet.saving") : t("referEarnSheet.save")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="re-share-row">
                  <div className="re-pill">
                    <button
                      type="button"
                      className="re-pill-code"
                      aria-label={t("referEarnSheet.yourCodeTapTo", { code })}
                      title={t("referEarnSheet.editCode")}
                      onClick={() => {
                        setDraft(code);
                        setEditing(true);
                      }}
                    >
                      <span className="re-pill-code-txt" ref={codeRef}>{code}</span>
                      <span className="re-pill-pen">{PENCIL_ICON}</span>
                    </button>
                    <button
                      type="button"
                      className={`re-pill-copy${compactCopy ? " is-round" : ""}`}
                      onClick={copy}
                      aria-label={compactCopy ? (copied ? t("referEarnSheet.copied") : t("referEarnSheet.copyLink")) : undefined}
                    >
                      {copied && compactCopy ? CHECK_ICON : COPY_ICON}
                      {!compactCopy && (copied ? t("referEarnSheet.copied") : t("referEarnSheet.copyLink"))}
                    </button>
                  </div>
                </div>
                <div className="re-code-link re-code-link--under">
                  <T k="referEarnSheet.join" vars={{ SITE, value: code.toLowerCase() }} tags={{ 0: (c) => <b>{c}</b> }} />
                </div>
              </>
            )}

            {/* Every rule, spelled out — each line is what the server does
                (lib/fanReferrals.js): points per APPROVED band or DJ, no
                limit on how many, points are never money. */}
            <section className={`re-terms-card${termsOpen ? " is-open" : ""}`}>
              <button type="button" className="re-terms-head" onClick={() => setTermsOpen((v) => !v)} aria-expanded={termsOpen}>
                <span>{t("referEarnSheet.yourTerms")}</span>
                <span className="re-terms-chev" aria-hidden="true">{CHEVRON_UP}</span>
              </button>
              {termsOpen && (
                // As short as a terms box can be (founder 10/30: "people hate
                // reading"): the 2% with no end date up to the cap, no limit on
                // how many, who pays, the 30 days, the points.
                <ul className="re-terms-list">
                  {money && (
                    <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="fanReferEarn.termPct" vars={{ amount: formatAmountForCopy(1000, "usd") }} tags={{ 0: (c) => <b>{c}</b>, 1: (c) => <b>{c}</b> }} /></span></li>
                  )}
                  <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="fanReferEarn.term2" tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
                  {money && (
                    <>
                      <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="referEarnSheet.term3" tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
                      <li><span className="re-terms-ic">{INFO_ICON}</span><span>{t("referEarnSheet.term4")}</span></li>
                    </>
                  )}
                  <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="fanReferEarn.term1" vars={{ stars: n(stats.starsEach) }} tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </BottomSheet>
    {cashingOut && money && (
      <FanCashoutFlow money={money} onClose={() => setCashingOut(false)} onChanged={() => setTick((x) => x + 1)} />
    )}
    </>
  );
}
